import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { FiHome, FiMessageSquare, FiBook, FiLogOut, FiUsers, FiSettings, FiFolder, FiBarChart2, FiMenu, FiX, FiAward, FiSliders } from 'react-icons/fi';
import { getAssetUrl } from '../config';

function Sidebar({ open, setOpen, isDesktop }) {
  const { user, logout } = useAuth();
  const { unreadCount, mentionUnreadCount } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const sidebarRef = useRef(null);

  // Close sidebar on route change (mobile only)
  useEffect(() => {
    if (!isDesktop) {
      setOpen(false);
    }
  }, [location.pathname, isDesktop, setOpen]);

  // Close sidebar on click outside (mobile only)
  useEffect(() => {
    function handleClickOutside(e) {
      if (
        !isDesktop &&
        open &&
        sidebarRef.current &&
        !sidebarRef.current.contains(e.target) &&
        !e.target.closest('[data-sidebar-toggle]')
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, isDesktop, setOpen]);

  // Close on Escape
  useEffect(() => {
    function handleEsc(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [setOpen]);

  const handleChatClick = () => {
    navigate('/chat');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    return getAssetUrl(imagePath);
  };

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(path);
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: FiHome, onClick: () => navigate('/dashboard') },
    {
      path: '/chat',
      label: 'Chat',
      icon: FiMessageSquare,
      onClick: handleChatClick,
      unreadCount,
      mentionUnreadCount,
    },
    { path: '/training', label: 'Training', icon: FiBook, onClick: () => navigate('/training') },
    { path: '/files', label: 'Files', icon: FiFolder, onClick: () => navigate('/files') },
    { path: '/reports', label: 'Reports', icon: FiBarChart2, onClick: () => navigate('/reports') },
  ];

  const adminItems = [
    { path: '/admin/training', label: 'Manage Training', icon: FiSettings, onClick: () => navigate('/admin/training') },
    { path: '/admin/users', label: 'Manage Users', icon: FiUsers, onClick: () => navigate('/admin/users') },
    { path: '/admin/completions', label: 'Training Completions', icon: FiAward, onClick: () => navigate('/admin/completions') },
    { path: '/admin/settings', label: 'Settings', icon: FiSliders, onClick: () => navigate('/admin/settings') },
  ];

  return (
    <>
      {/* Top bar with burger icon */}
      <header
        className={`glass-nav sticky top-0 z-40 flex items-center h-14 sm:h-16 px-3 sm:px-4 lg:px-6 transition-all duration-300 ease-in-out overflow-x-hidden box-border ${
          open && isDesktop ? 'lg:ml-72 lg:w-[calc(100%-18rem)]' : 'ml-0 w-full'
        }`}
      >
        <button
          data-sidebar-toggle="true"
          onClick={() => setOpen(!open)}
          className="p-2 rounded-xl text-gray-700 hover:bg-white/50 transition-all duration-200"
          aria-label="Toggle menu"
        >
          <FiMenu className="w-6 h-6" />
        </button>
        {!open && (
          <h1
            onClick={() => navigate('/dashboard')}
            className="ml-3 text-lg sm:text-xl font-bold text-[#3c79bb] cursor-pointer hover:opacity-80 transition-opacity"
          >
            PW Connect
          </h1>
        )}

        {/* User avatar (right side) */}
        <button
          onClick={() => navigate('/profile')}
          className="ml-auto flex items-center gap-2 px-2 sm:px-3 py-1.5 glass-light hover:glass rounded-xl transition-all duration-200"
        >
          {user?.profileImage ? (
            <img
              src={getImageUrl(user.profileImage)}
              alt={user.username}
              className="h-7 w-7 sm:h-8 sm:w-8 rounded-full object-cover ring-2 ring-white/50"
            />
          ) : (
            <div className="h-7 w-7 sm:h-8 sm:w-8 rounded-full bg-[#3c79bb] flex items-center justify-center text-white text-xs sm:text-sm font-semibold ring-2 ring-white/50">
              {user?.firstName ? user.firstName[0] : user?.username?.[0]?.toUpperCase() || 'U'}
            </div>
          )}
          <div className="hidden sm:flex flex-col items-start">
            <span className="text-gray-900 font-semibold text-xs sm:text-sm">
              {user?.firstName || user?.lastName
                ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                : user?.username}
            </span>
            <span className="px-1.5 py-0.5 text-[10px] sm:text-xs font-semibold rounded-full bg-[#3c79bb] text-white">
              {user?.role?.toUpperCase()}
            </span>
          </div>
        </button>
      </header>

      {/* Overlay (mobile only) */}
      {!isDesktop && (
        <div
          className={`fixed inset-0 bg-black/30 z-40 transition-opacity duration-300 ${
            open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
          onClick={() => setOpen(false)}
        />
      )}

      {/* Sidebar drawer */}
      <aside
        ref={sidebarRef}
        className={`fixed top-0 left-0 z-50 h-full w-72 glass-sidebar transform transition-transform duration-300 ease-in-out flex flex-col ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Sidebar header */}
        <div className="flex items-center h-14 sm:h-16 px-4 border-b border-white/20">
          <h2
            onClick={() => navigate('/dashboard')}
            className="text-lg font-bold text-[#3c79bb] cursor-pointer hover:opacity-80 transition-opacity"
          >
            PW Connect
          </h2>
          <button
            onClick={() => setOpen(false)}
            className="ml-auto p-2 rounded-xl text-gray-600 hover:bg-white/50 transition-all duration-200"
            aria-label="Close menu"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Nav links */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          <ul className="flex flex-col gap-1">
            {navItems.map((item) => (
              <li key={item.path}>
                <button
                  onClick={item.onClick}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-left transition-all duration-200 ${
                    isActive(item.path)
                      ? 'bg-[#3c79bb] text-white shadow-lg shadow-[#3c79bb]/30'
                      : 'text-gray-800 hover:bg-white/50 font-medium'
                  }`}
                >
                  <item.icon className="text-lg flex-shrink-0" />
                  <span className="flex-1 flex items-center justify-between gap-2 min-w-0">
                    <span className="truncate">{item.label}</span>
                    {item.path === '/chat' && (item.mentionUnreadCount > 0 || item.unreadCount > 0) && (
                      <span className="flex flex-shrink-0 items-center gap-1">
                        {item.mentionUnreadCount > 0 && (
                          <span
                            className="min-w-[1.25rem] h-6 px-1 inline-flex items-center justify-center gap-0.5 bg-amber-500 text-white text-[10px] font-bold rounded-full shadow-sm"
                            title="Mentions"
                          >
                            @
                            {item.mentionUnreadCount > 99 ? '99+' : item.mentionUnreadCount}
                          </span>
                        )}
                        {item.unreadCount > 0 && (
                          <span className="min-w-[1.25rem] h-6 px-1.5 inline-flex items-center justify-center bg-red-500 text-white text-[11px] font-bold rounded-full shadow-sm">
                            {item.unreadCount > 99 ? '99+' : item.unreadCount}
                          </span>
                        )}
                      </span>
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>

          {user?.role === 'admin' && (
            <>
              <div className="mt-6 mb-2 px-4">
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Admin</span>
              </div>
              <ul className="flex flex-col gap-1">
                {adminItems.map((item) => (
                  <li key={item.path}>
                    <button
                      onClick={item.onClick}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-left transition-all duration-200 ${
                        isActive(item.path)
                          ? 'bg-[#3c79bb] text-white shadow-lg shadow-[#3c79bb]/30'
                          : 'text-gray-800 hover:bg-white/50 font-medium'
                      }`}
                    >
                      <item.icon className="text-lg flex-shrink-0" />
                      <span>{item.label}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </nav>

        {/* Sidebar footer – Profile & Logout */}
        <div className="border-t border-white/20 p-3 flex flex-col gap-1">
          <button
            onClick={() => navigate('/profile')}
            className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-left text-gray-800 hover:bg-white/50 transition-all duration-200 font-medium"
          >
            {user?.profileImage ? (
              <img
                src={getImageUrl(user.profileImage)}
                alt={user.username}
                className="h-8 w-8 rounded-full object-cover ring-2 ring-white/50"
              />
            ) : (
              <div className="h-8 w-8 rounded-full bg-[#3c79bb] flex items-center justify-center text-white text-sm font-semibold ring-2 ring-white/50">
                {user?.firstName ? user.firstName[0] : user?.username?.[0]?.toUpperCase() || 'U'}
              </div>
            )}
            <div className="flex flex-col">
              <span className="text-sm font-semibold text-gray-900">
                {user?.firstName || user?.lastName
                  ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                  : user?.username}
              </span>
              <span className="text-xs text-gray-500">{user?.role?.toUpperCase()}</span>
            </div>
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-left text-red-600 hover:bg-red-50/50 transition-all duration-200 font-medium"
          >
            <FiLogOut className="text-lg" />
            <span>Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
}

export default Sidebar;
